import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Send, Sparkles, Paperclip, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { programmesApi, whatsappApi } from "@/lib/crmApi";

const cleanPhone = (p = "") => p.replace(/[\s().-]/g, "");
const waLink = (p, msg) =>
  `https://wa.me/${cleanPhone(p).replace(/^\+?/, "")}${msg ? `?text=${encodeURIComponent(msg)}` : ""}`;

const TEMPLATES = [
  {
    id: "intro",
    label: "Premier contact",
    icon: "👋",
    body: (lead) =>
      `Bonjour ${lead.nom},\n\nJe suis commercial chez SOGI SARL et je reviens vers vous suite à votre intérêt pour nos programmes immobiliers.\n\nQuel jour seriez-vous disponible pour un échange rapide afin de vous présenter les biens disponibles ?\n\nBien à vous`,
  },
  {
    id: "catalogue",
    label: "Envoi catalogue",
    icon: "📁",
    // When the user picks this template we auto-tick the "attach catalog"
    // box in the dialog (handled below in selectTemplate).
    autoAttachCatalog: true,
    body: (lead) =>
      `Bonjour ${lead.nom},\n\nComme convenu, voici notre catalogue détaillé${lead.programme_immobilier ? ` du programme ${lead.programme_immobilier}` : ""}. Vous y trouverez les plans, les superficies et les prix.\n\nN'hésitez pas à me contacter pour toute question.\n\nCordialement`,
  },
  {
    id: "rdv",
    label: "Confirmation RDV",
    icon: "📅",
    body: (lead) =>
      `Bonjour ${lead.nom},\n\nJe vous confirme notre rendez-vous pour la visite de l'appartement. Notre commercial vous attendra sur place.\n\nÀ très bientôt,\nSOGI SARL`,
  },
  {
    id: "relance",
    label: "Relance douce",
    icon: "💬",
    body: (lead) =>
      `Bonjour ${lead.nom},\n\nJe me permets de revenir vers vous concernant votre projet immobilier. Avez-vous eu le temps de consulter les informations que je vous ai transmises ?\n\nJe reste à votre disposition pour toute question.\n\nBien cordialement`,
  },
  {
    id: "financement",
    label: "Plan de financement",
    icon: "💰",
    body: (lead) =>
      `Bonjour ${lead.nom},\n\nSuite à notre échange, voici un plan de financement adapté à votre budget${lead.budget ? ` (${lead.budget})` : ""}.\n\nJe vous propose un appel cette semaine pour vous expliquer les modalités. Quel jour vous convient ?\n\nCordialement`,
  },
  {
    id: "closing",
    label: "Closing",
    icon: "🤝",
    body: (lead) =>
      `Bonjour ${lead.nom},\n\nFélicitations pour votre choix ! Pour finaliser votre acquisition, voici les prochaines étapes :\n\n1. Signature du contrat de réservation\n2. Versement de l'acompte\n3. Démarches administratives\n\nQuel créneau vous arrange pour la signature ?\n\nÀ très vite,\nSOGI SARL`,
  },
];

export default function WhatsAppQuickAction({ lead, onSent, asButton = true }) {
  const [open, setOpen] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState("intro");
  const [body, setBody] = useState(() => TEMPLATES[0].body(lead));

  // --- Catalog attachment state ---------------------------------------
  // The user can tick "Joindre le catalogue" to send the programme catalog
  // PDF along with the message (sent as caption). Sent via the Baileys API
  // — wa.me deeplinks can't carry attachments.
  //
  // The catalog block is ONLY exposed for first contact: once we have any
  // WhatsApp message history with this lead, the catalog has presumably
  // been sent already and re-attaching it would be spammy. The user can
  // still send the catalog later via the dedicated "Envoyer catalogue"
  // button on the lead detail page.
  const [attachCatalog, setAttachCatalog] = useState(false);
  const [programmes, setProgrammes] = useState([]);
  const [selectedProgId, setSelectedProgId] = useState("");
  const [loadingProgs, setLoadingProgs] = useState(false);
  const [sending, setSending] = useState(false);
  const [hasPreviousMessages, setHasPreviousMessages] = useState(false);
  const [checkingHistory, setCheckingHistory] = useState(false);

  // Fetch programmes (with catalog) AND the WA message history when the
  // dialog opens. Both are cheap calls and only run on dialog open.
  useEffect(() => {
    if (!open) return;
    setLoadingProgs(true);
    setCheckingHistory(true);
    programmesApi
      .list()
      .then((all) => {
        const withCatalog = (all || []).filter((p) => !!p.catalog_filename && (p.catalog_size || 0) > 0);
        setProgrammes(withCatalog);
        // Default to the lead's preferred programme if it has a catalog,
        // otherwise the first available one.
        const fav = withCatalog.find((p) => p.nom === lead?.programme_immobilier);
        setSelectedProgId(fav?.id || withCatalog[0]?.id || "");
      })
      .catch(() => setProgrammes([]))
      .finally(() => setLoadingProgs(false));

    // Probe the WA conversation: if any message exists (incoming or
    // outgoing), this is no longer a "first contact" so we hide the
    // catalog block.
    const query = lead?.id ? { leadId: lead.id } : { phone: lead?.telephone };
    if (!query.leadId && !query.phone) {
      setCheckingHistory(false);
      setHasPreviousMessages(false);
      return;
    }
    whatsappApi
      .messages({ ...query, limit: 1 })
      .then((data) => {
        setHasPreviousMessages((data?.items || []).length > 0);
      })
      .catch(() => {
        // If the WA service is down we can't tell — be conservative and
        // assume it's a first contact (don't hide a useful action).
        setHasPreviousMessages(false);
      })
      .finally(() => setCheckingHistory(false));
  }, [open, lead?.id, lead?.telephone, lead?.programme_immobilier]);

  const selectedProg = useMemo(
    () => programmes.find((p) => p.id === selectedProgId) || null,
    [programmes, selectedProgId]
  );
  const noCatalogAvailable = !loadingProgs && programmes.length === 0;
  // Catalog attachment is offered only for the very first WhatsApp contact
  // with this lead. After that, the bloc is hidden entirely (the dedicated
  // "Envoyer catalogue" button on the lead detail page is the right tool).
  // While the history probe is in flight we ALSO hide the block to avoid
  // a flash of the bloc that then disappears.
  const showCatalogBlock = !checkingHistory && !hasPreviousMessages;

  const selectTemplate = (t) => {
    setActiveTemplate(t.id);
    setBody(t.body(lead));
    // The "Envoi catalogue" template auto-ticks the attach checkbox so the
    // user doesn't have to remember a second click — but only on first
    // contact (otherwise the checkbox isn't even rendered).
    if (t.autoAttachCatalog && !noCatalogAvailable && showCatalogBlock) {
      setAttachCatalog(true);
    }
  };

  // Reset the dialog state each time it reopens — avoids carrying a
  // ticked checkbox or an unsent message from a previous lead.
  // IMPORTANT: depend on `lead?.id`, NOT the whole `lead` object — parent
  // re-renders rebuild the lead object via spreads, so a stable id avoids
  // wiping the user's draft mid-edit on every unrelated parent state change.
  useEffect(() => {
    if (open) {
      setActiveTemplate("intro");
      setBody(TEMPLATES[0].body(lead));
      setAttachCatalog(false);
      setSending(false);
      setHasPreviousMessages(false);
      setCheckingHistory(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lead?.id]);

  const send = async () => {
    if (!lead?.telephone) {
      toast.error("Ce lead n'a pas de numéro de téléphone");
      return;
    }
    if (!body.trim()) {
      toast.error("Le message est vide");
      return;
    }

    // Path A: attach catalog → use the backend API so the file goes with
    // the message as a caption. We can't do this through wa.me (deep link
    // doesn't support attachments). Belt-and-braces: also gate on
    // showCatalogBlock in case state went out of sync.
    if (attachCatalog && showCatalogBlock) {
      if (!selectedProgId) {
        toast.error("Choisis un programme avec catalogue à joindre");
        return;
      }
      setSending(true);
      try {
        await programmesApi.sendCatalogToLead(lead.id, {
          programme_id: selectedProgId,
          caption: body.trim(),
        });
        toast.success(
          `Catalogue + message envoyés à ${lead.nom} sur WhatsApp`
        );
        onSent?.(body);
        setOpen(false);
      } catch (e) {
        const detail =
          e?.response?.data?.detail?.message ||
          e?.response?.data?.detail ||
          e?.humanMessage ||
          "Échec de l'envoi WhatsApp";
        toast.error(typeof detail === "string" ? detail : "Échec de l'envoi");
      } finally {
        setSending(false);
      }
      return;
    }

    // Path B: text-only → also use the API (consistent UX, message is
    // logged in the conversation history). Falls back to the wa.me deep
    // link if the API call fails (e.g. WA disconnected) so the user can
    // still send manually.
    setSending(true);
    try {
      await whatsappApi.send({
        phone: lead.telephone,
        text: body,
        lead_id: lead.id,
      });
      toast.success(`Message envoyé à ${lead.nom} sur WhatsApp`);
      onSent?.(body);
      setOpen(false);
    } catch (e) {
      // Last-resort: open WhatsApp Web with the message pre-filled so the
      // user can send it manually. Useful when the sidecar is down.
      window.open(waLink(lead.telephone, body), "_blank", "noopener");
      const detail =
        e?.response?.data?.detail?.message ||
        e?.humanMessage ||
        "Envoi via API impossible — WhatsApp Web ouvert pour envoi manuel";
      toast.warning(typeof detail === "string" ? detail : "WhatsApp Web ouvert");
      onSent?.(body);
      setOpen(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {asButton && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => setOpen(true)}
          data-testid="wa-quick-action-btn"
          className="gap-1.5"
        >
          <MessageCircle className="h-3.5 w-3.5 text-emerald-600" />
          WhatsApp
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-emerald-600" />
              WhatsApp · {lead.nom}
            </DialogTitle>
            <DialogDescription>
              Choisissez un modèle, personnalisez votre message et — au besoin —
              joignez le catalogue PDF du programme.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 sm:grid-cols-[200px_1fr]">
            {/* Templates list */}
            <div className="space-y-1.5">
              <div className="text-label mb-2 text-muted-foreground flex items-center gap-1">
                <Sparkles className="h-3 w-3" />
                Modèles
              </div>
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  onClick={() => selectTemplate(t)}
                  data-testid={`wa-template-${t.id}`}
                  // Specific properties only (no `transition-all`): colors for
                  // hover, transform for press feedback. `active:scale-[0.96]`
                  // gives tactile feedback without feeling exaggerated.
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs",
                    "transition-[background-color,color,transform] duration-150 active:scale-[0.96]",
                    activeTemplate === t.id
                      ? "bg-accent-navy text-white dark:bg-primary dark:text-primary-foreground"
                      : "hover:bg-muted"
                  )}
                >
                  <span className="text-base">{t.icon}</span>
                  <span className="font-medium">{t.label}</span>
                </button>
              ))}
            </div>

            {/* Editor */}
            <div className="space-y-2">
              <div className="text-label text-muted-foreground">Message</div>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={10}
                className="font-mono text-xs leading-relaxed"
                data-testid="wa-message-textarea"
              />
              <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                {/* tabular-nums prevents horizontal jitter as the counter ticks
                    while the user types. */}
                <span className="tabular-nums">{body.length} caractères</span>
                <span className="truncate font-mono tabular-nums">→ {lead.telephone}</span>
              </div>

              {/* Catalog attachment block — first contact only.
                  Once a message has been exchanged with this lead, the
                  block is hidden entirely (no notice, no checkbox).
                  The dedicated "Envoyer catalogue" button on the lead
                  detail page stays available for later resends. */}
              {showCatalogBlock && (
              <div
                className={cn(
                  "mt-2 rounded-lg border-2 p-3 transition-colors",
                  attachCatalog
                    ? "border-emerald-300 bg-emerald-50/50 dark:border-emerald-900/40 dark:bg-emerald-950/20"
                    : "border-border/60 bg-muted/20"
                )}
              >
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={attachCatalog}
                    onChange={(e) => setAttachCatalog(e.target.checked)}
                    disabled={noCatalogAvailable}
                    className="mt-0.5 h-4 w-4 accent-emerald-600"
                    data-testid="wa-attach-catalog"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-sm font-semibold">
                      <Paperclip className="h-3.5 w-3.5 text-emerald-600" />
                      Joindre le catalogue PDF
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      {loadingProgs
                        ? "Chargement des catalogues disponibles…"
                        : noCatalogAvailable
                        ? "Aucun catalogue disponible. Ajoute-en un dans Paramètres → Catalogue programmes."
                        : "Le message sera envoyé en légende du PDF (un seul message côté client)."}
                    </p>
                  </div>
                </label>

                {attachCatalog && !noCatalogAvailable && (
                  <div className="mt-3 space-y-1.5 pl-6">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      Programme à envoyer
                    </div>
                    <Select
                      value={selectedProgId}
                      onValueChange={setSelectedProgId}
                    >
                      <SelectTrigger
                        className="h-8 text-xs"
                        data-testid="wa-attach-prog-select"
                      >
                        <SelectValue placeholder="Choisir un programme" />
                      </SelectTrigger>
                      <SelectContent>
                        {programmes.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            <span className="flex items-center gap-2">
                              <FileText className="h-3 w-3 text-emerald-600" />
                              {p.nom}
                              <span className="text-[10px] text-muted-foreground">
                                ({Math.round((p.catalog_size || 0) / 1024)} KB)
                              </span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedProg && (
                      <p className="text-[10px] text-muted-foreground">
                        📁 {selectedProg.catalog_filename}
                      </p>
                    )}
                  </div>
                )}
              </div>
              )}
            </div>
          </div>

          <div className="mt-2 flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={sending}
              // Scale-on-press for tactile feedback (only on enabled state).
              className="transition-transform active:scale-[0.96] disabled:active:scale-100"
            >
              Annuler
            </Button>
            <Button
              onClick={send}
              disabled={sending || !body.trim()}
              data-testid="wa-send-btn"
              className="gap-2 bg-emerald-600 text-white transition-[background-color,transform] hover:bg-emerald-700 active:scale-[0.96] disabled:active:scale-100"
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Envoi…
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  {attachCatalog
                    ? "Envoyer message + catalogue"
                    : "Envoyer via WhatsApp"}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
